/**
 * Tasks library — data access (DEC-0452, DEC-0045). One read of every `task`
 * instance plus the blocked_by arcs among them, so the page can derive status
 * family, score and Blocked without a query per row.
 */
import { supabase } from '@/lib/supabaseClient';
import {
  BLOCKED_BY_RELATION,
  arcsFromRelations,
  type BlockingArc,
  type BlockingRelationRow,
} from '@/lib/entities/taskBlocking';
import type { TaskRecord } from '@/lib/entities/taskLibrary';

export const TASK_ENTITY_TYPE = 'task';

export interface TaskLibraryData {
  records: TaskRecord[];
  arcs: BlockingArc[];
}

const PAGE = 1000;

/** Supabase caps one response (1000 rows by default); page until a short page so
 *  the library never silently drops tasks past the cap. */
async function fetchAll<T>(page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await page(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    all.push(...(data ?? []));
    if (!data || data.length < PAGE) return all;
  }
}

export async function fetchTaskLibrary(): Promise<TaskLibraryData> {
  const records = (await fetchAll((from, to) =>
    supabase
      .from('vb_records')
      .select('id, title, meta')
      .eq('entity_type', TASK_ENTITY_TYPE)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .order('id')
      .range(from, to),
  )) as TaskRecord[];
  if (records.length === 0) return { records, arcs: [] };

  // The arc table is small next to the tasks; one read of every blocked_by row
  // is cheaper than chunking an .in() over thousands of ids. Arcs whose source
  // is not a task are dropped here.
  // A failed arc read throws: showing every task as unblocked would be wrong.
  const rels = await fetchAll((from, to) =>
    supabase
      .from('note_relations')
      .select('source_id, target_id, relation_type')
      .eq('relation_type', BLOCKED_BY_RELATION)
      .order('id')
      .range(from, to),
  );
  const ids = new Set(records.map((r) => r.id));
  const arcs = arcsFromRelations(rels as BlockingRelationRow[]).filter((a) => ids.has(a.taskId));
  return { records, arcs };
}
